import React, { useCallback, useRef } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import utf8 from 'utf8'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { restartPolicyPolling } from 'src/pages/Policies/Policies.hooks'
import { useConfirmationContext } from 'src/Confirmation/ConfirmationContext'
import {
  AddPolicyDocumentRequest,
  ReplacePolicyDocumentRequest,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import log from 'loglevel'
import {
  RichTextEditor,
  RteModal,
  RteModalQueryParams,
} from '../../../../../components/RichTextEditor'

type DocumentMarkdownEditorProps = {
  id?: string
  modelId?: string
  title: string
  markdown: string
  onUpdate: () => void
}

export const DocumentItemMarkdownEditor = (
  props: DocumentMarkdownEditorProps,
): JSX.Element => {
  const location = useLocation()
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)
  const { setConfirmationState } = useConfirmationContext()
  const shouldRegenerateDescriptionRef = useRef<boolean>(true)

  const confirmDescriptionRegeneration =
    useCallback(async (): Promise<boolean> => {
      return await new Promise<boolean>((resolve) => {
        setConfirmationState({
          isShown: true,
          size: 'sm',
          title: 'Regenerate description?',
          body: 'Do you want to regenerate the policy description from this updated template?',
          confirmText: 'Regenerate',
          cancelText: "Don't Regenerate",
          onConfirmCB: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
    }, [setConfirmationState])

  const confirmBeforeSave = useCallback(async (): Promise<boolean> => {
    const shouldRegenerateDescription = await confirmDescriptionRegeneration()
    shouldRegenerateDescriptionRef.current = shouldRegenerateDescription
    return true
  }, [confirmDescriptionRegeneration])

  if (!props.modelId && props.id) {
    return <></>
  }

  const query = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })

  const objectId = props.id ?? props.modelId ?? ''
  const show =
    query[RteModalQueryParams.SHOW_RTE_MODAL] === 'true' &&
    query[RteModalQueryParams.UNIQUE_ID] === objectId

  return (
    <RteModal show={show}>
      <RichTextEditor
        requiredCapabilities={[CAPABILITY.CAN_UPDATE_POLICY_DOCS]}
        objectId={objectId}
        title={props.title}
        markdown={props.markdown}
        isEditableTitle
        confirmCB={confirmBeforeSave}
        saveCB={async (title, body) => {
          const regenerateDescription = shouldRegenerateDescriptionRef.current
          const documentRequest = new AddPolicyDocumentRequest()
            .setPolicyModelId(props.modelId ?? '')
            .setMime('text/markdown')
            .setBody(
              new Uint8Array(
                Array.from(utf8.encode(body)).map((s) => s.charCodeAt(0)),
              ),
            )
            .setCaption(title)
            .setRegenerateDescription(
              new BoolValue().setValue(regenerateDescription),
            )
          try {
            const addedDocument = props.id
              ? await policyClient.replacePolicyDocument(
                  new ReplacePolicyDocumentRequest()
                    .setOldPolicyDocumentId(objectId)
                    .setDocument(documentRequest),
                )
              : await policyClient.addPolicyDocument(documentRequest)
            props.onUpdate()
            restartPolicyPolling()
            return addedDocument
          } catch (err) {
            log.error(
              `Error when saving policy documentId: ${objectId} in policyId: ${props.modelId}`,
              err,
            )
          }
        }}
      />
    </RteModal>
  )
}
